import DOMPurify from "dompurify";

const SORRY_TEXT = "<p>We're sorry, but we are experiencing a temporary service disruption. We expect this to be fixed soon. In the meantime, you can still get your questions answered by calling our licensed experts at (855) 734-5291.</p>";

const productIdMapping = {
  "Contractor's Equipment and Small Tools": "IM",
  "Equipment and Business Property": "IM",
  "General Liability": "GL",
  "Workers' Compensation": "WC",
  "Business Owner's Policy": "BOP",
  "Professional Liability / Errors & Omissions": "PL"
};

function getChatElements(chatWrapper) {
  return {
    chatInput: chatWrapper.querySelector('.js-reco-chat-input'),
    sendIcon: chatWrapper.querySelector('.reco-chat-send-icon'),
    messagesInner: chatWrapper.querySelector('.reco-chat-messages-inner'),
    loaderWrapper: chatWrapper.querySelector('.reco-chat-loader-wrapper'),
    loaderText: chatWrapper.querySelector('.reco-chat-loader-text'),
    typingBubblesWrapper: chatWrapper.querySelector('.reco-chat-typing-bubbles-wrapper'),
    promptsOuter: chatWrapper.querySelector('.reco-chat-prompts-outer'),
    downArrow: chatWrapper.querySelector('.js-reco-chat-down-arrow'),
    inputWrapper: chatWrapper.querySelector('.reco-chat-input-wrapper'),
    introElem: chatWrapper.querySelector('.reco-agent-intro'),
    promptsContainer: chatWrapper.querySelector('.reco-chat-prompts.notranslate'),
    textarea: chatWrapper.querySelector('textarea')
  };
}

function getModalElements(chatWrapper) {
  const animateContainer = chatWrapper.closest(
    '.mobius-dialogue-modal__description.mobius-dialogue-modal__description_drawer'
  );
  const chatContainer = animateContainer?.querySelector(
    '.mobius-dialogue-modal__styled-drawer-content'
  );
  const drawerContent = chatWrapper.closest('.mobius-drawer__content');
  const drawerHeader = drawerContent?.previousElementSibling;

  return {
    animateContainer,
    chatContainer,
    drawerContent,
    drawerHeader
  };
}

function startChat(chatWrapper) {
  let sessionId = chatWrapper.dataset.chatSessionId;
  const product = chatWrapper.dataset.product;

  if (!sessionId) {
    sessionId = generateSessionId(product);
    chatWrapper.dataset.chatSessionId = sessionId;
  }

  createChatApi({
    primaryTrade: chatWrapper.dataset.primaryTrade,
    sessionId: sessionId,
    productName: product,
    productCode: getProductCodeLowercase(product),
    experiments: JSON.parse(chatWrapper.dataset.experiments)
  })
    .then((json) => {
      chatWrapper.dataset.chatId = json.chat_id;
      chatWrapper.dataset.authentication = JSON.stringify(json.authentication);

      renderChatResponse(chatWrapper, json.messages);
      enableInputAndUpdateSendIcon(chatWrapper);
    })
    .catch(() => {
      renderErrorMessage(chatWrapper);
    });
}

function sendChatMessage(message, chatWrapper, chatInput) {
  if (message.trim().length === 0) {
    return;
  }

  if (chatWrapper.dataset.connectedToLiveChat === 'true') {
    ws.send(message);
    const sanitizedMessage = DOMPurify.sanitize(`<p>${message}</p>`);
    renderUserMessage(chatWrapper, sanitizedMessage);
    showTypingBubbles(chatWrapper);
    chatInput.value = "";

    sendLiveChatMessageSentEvent(chatWrapper, message, "customer");
    return;
  }

  disableInputAndSendIcon(chatWrapper);

  let sessionId = chatWrapper.dataset.chatSessionId;
  const product = chatWrapper.dataset.product;

  if (!sessionId) {
    sessionId = generateSessionId(product);
    chatWrapper.dataset.chatSessionId = sessionId;
  }

  const chatElements = getChatElements(chatWrapper);
  const { drawerHeader } = getModalElements(chatWrapper);
  const isFirstMsg = !chatElements.messagesInner.querySelector(".reco-chat-message");

  hideQuickReplies(chatWrapper);

  chatInput.value = "";

  // TODO: This is for IP workflow, maybe should be handled on the backend
  // like what if we return workflow name from the backend
  if (message.trim() === "What price ranges can I expect?") {
    chatWrapper.dataset.ipWorkflow = 'true';
  }

  showLoader(chatWrapper, chatWrapper.dataset.ipWorkflow === 'true' ? "" : undefined);

  // TODO: This is for IP workflow
  if (message.trim() === "Back to questions") {
    chatWrapper.dataset.ipWorkflow = 'false';
  }

  renderUserMessage(chatWrapper, message);

  scrollToBottom(chatWrapper);

  if (isFirstMsg) {
    minimizeDrawerHeader(drawerHeader);
  }

  moveIntroToMessages(chatWrapper);

  const lastQuickReplies = getLastQuickReplies(chatWrapper);

  sendMessageToApi({
    chatId: chatWrapper.dataset.chatId,
    authentication: JSON.parse(chatWrapper.dataset.authentication),
    userMessage: createUserMessagePayload(lastQuickReplies, message)
  })
    .then((json) => {
      enableInputAndUpdateSendIcon(chatWrapper);

      renderChatResponse(chatWrapper, json.messages);
      chatWrapper.dataset.lastQuickReplies = JSON.stringify(json.messages.find(e => e.type == 'radio_options') ?? {});

      showQuickReplies(chatWrapper);

      hideLoader(chatWrapper);

      scrollToRecentMessage(chatWrapper);
    })
    .catch(() => {
      renderErrorMessage(chatWrapper);
      enableInputAndUpdateSendIcon(chatWrapper);
      hideLoader(chatWrapper);
    });
}

const createUserMessagePayload = (lastQuickReplies, message) => {
  const selectedMessage = lastQuickReplies.options?.find(e => e.text === message);
  if (selectedMessage) {
    selectedMessage.selected = true;
    lastQuickReplies.role = "user";

    return lastQuickReplies;
  }

  return {
    text: message,
    role: "user",
    type: "text"
  }
}

function clearSkeletonPlaceholder(chatWrapper) {
  const chatElements = getChatElements(chatWrapper);

  const skeletonElem = chatElements.introElem?.querySelector(".reco-agent-intro-skeleton");
  if (skeletonElem) {
    skeletonElem.remove();
  }

  const promptsContainer = chatWrapper.querySelector('.reco-chat-prompts');
  if (promptsContainer) {
    while (promptsContainer.firstChild) {
      promptsContainer.removeChild(promptsContainer.firstChild);
    }
  }
}

function renderTextMessage(chatWrapper, message) {
  const chatElements = getChatElements(chatWrapper);

  if (message.metadata?.display?.intro) {
    clearSkeletonPlaceholder(chatWrapper);
  }

  if (message.metadata?.display?.heading) {
    const headingHtml = DOMPurify.sanitize(`<h3>${message.text}</h3>`);
    chatElements.introElem.insertAdjacentHTML("beforeend", headingHtml);
    return;
  }

  if (message.metadata?.display?.initial) {
    const initialMessageHtml = DOMPurify.sanitize(
      `<div class="reco-chat-intro-after-read-hidden">${message.text}</div>`
    );
    chatElements.introElem.insertAdjacentHTML("beforeend", initialMessageHtml);

    if (message.metadata?.display?.extended_text) {
      const extendedMessageHtml = DOMPurify.sanitize(
        `<div class="reco-chat-intro-hidden" style="display: none;">${message.metadata.display.extended_text}</div>`
      );
      chatElements.introElem.insertAdjacentHTML("beforeend", extendedMessageHtml);

      const product = DOMPurify.sanitize(chatWrapper.dataset.product);
      const learnMoreHtml = DOMPurify.sanitize(
        `<a href="#" class="reco-chat-read-more reco-chat-intro-after-read-hidden" data-product="${product}">Learn more</a>`,
        { ADD_ATTR: ['href', 'class', 'data-product'] }
      );
      chatElements.introElem.insertAdjacentHTML("beforeend", learnMoreHtml);
    }

    return;
  }

  let agentMsgHtml = DOMPurify.sanitize(
    `<div class="reco-chat-message chat-message-agent">${message.text}</div><div class="clearfix"></div>`
  );

  const { startApplicationExperiment } = chatElements.textarea.dataset;
  const product = chatWrapper.dataset.product;

  if (shouldShowApplicationHandoff(message, startApplicationExperiment)) {
    const handoff = DOMPurify.sanitize(
      `<div class="reco-chat-message chat-message-agent"><p>Ready to get started? Start your <a id="products-application-handoff" href="#" style="text-decoration: underline;">${product} application</a></p></div><div class="clearfix"></div><br />`,
    );
    agentMsgHtml += handoff;
  }

  chatElements.messagesInner.insertAdjacentHTML("beforeend", agentMsgHtml);
}

function renderUserMessage(chatWrapper, message) {
  const chatElements = getChatElements(chatWrapper);
  const userMsgHtml = DOMPurify.sanitize(
    `<div class="reco-chat-message chat-message-user">${message}</div><div class="clearfix"></div>`,
  );
  chatElements.messagesInner.insertAdjacentHTML("beforeend", userMsgHtml);
}

function renderTextAnswer(chatWrapper, message) {
  clearSkeletonPlaceholder(chatWrapper);

  const chatElements = getChatElements(chatWrapper);
  let agentMsgHtml = DOMPurify.sanitize(
    `<div class="reco-chat-message chat-message-agent">${message.text}</div><div class="clearfix"></div>`,
  );

  const { startApplicationExperiment } = chatElements.textarea.dataset;
  const product = chatWrapper.dataset.product;

  if (shouldShowApplicationHandoff(message, startApplicationExperiment)) {
    const handoff = DOMPurify.sanitize(
      `<div class="reco-chat-message chat-message-agent"><p>Ready to get started? Start your <a id="products-application-handoff" href="#" style="text-decoration: underline;">${product} application</a></p></div><div class="clearfix"></div><br />`,
    );
    agentMsgHtml += handoff;
  }

  chatElements.messagesInner.insertAdjacentHTML("beforeend", agentMsgHtml);
}

function renderQuickReplies(chatWrapper, quickReplies) {
  if (!quickReplies) return;

  if (quickReplies.disable_freetext) {
    disableInputAndSendIcon(chatWrapper);
  }

  const chatElements = getChatElements(chatWrapper);
  const optionsHtml = DOMPurify.sanitize(
    `<ul>
      ${quickReplies.options
      .map((option) => {
        const sanitizedText = DOMPurify.sanitize(option.text);
        return `<li data-prompt="${sanitizedText}">${sanitizedText}</li><br />`;
      })
      .join('')}
    </ul>`,
    { ADD_TAGS: ['li', 'ul', 'br'], ADD_ATTR: ['data-prompt'] }
  );

  clearQuickReplies(chatWrapper);

  chatElements.promptsContainer.insertAdjacentHTML('afterbegin', optionsHtml);

  chatWrapper.dataset.lastQuickReplies = JSON.stringify(quickReplies ?? {});
}

function renderListOfIndicativePrices(chatWrapper, pricesData) {
  const chatElements = getChatElements(chatWrapper);
  const pricesHtml = DOMPurify.sanitize(
    `<div class="reco-chat-message chat-message-agent">
      <ul class="reco-chat-indicative-prices">
        ${pricesData.prices.map(price => `<li class="reco-chat-indicative-prices__price">
          <div><span class="reco-chat-indicative-prices__carrier-name">${price.carrier_name}</span> <span class="reco-chat-indicative-prices__rating">${price.rating}</span></div>
          <div><span class="reco-chat-indicative-prices__monthly-premium">$${price.monthly_premium}</span><span class="reco-chat-indicative-prices__monthly-premium-frequency">/mo</span></div>
        </li>`).join('')}
      </ul>
    </div>
    <div class="clearfix"></div>
  `);

  chatElements.messagesInner.insertAdjacentHTML('beforeend', pricesHtml);
}

function renderChatResponse(chatWrapper, messages) {
  messages.forEach((message) => {
    if (message.type === "text") {
      renderTextMessage(chatWrapper, message);

      if (message.metadata && message.metadata.has_indicative_prices) {
        // TODO: This is for IP workflow, should be properly handled on the backend
        chatWrapper.dataset.ipWorkflow = 'false';

        renderListOfIndicativePrices(chatWrapper, message.metadata);
      }
    }
    else if (message.type === "radio_options") {
      renderQuickReplies(chatWrapper, message);
    }
  });
}

function clearQuickReplies(chatWrapper) {
  if (!chatWrapper) return;

  const chatElements = getChatElements(chatWrapper);
  if (chatElements.promptsContainer) {
    while (chatElements.promptsContainer.firstChild) {
      chatElements.promptsContainer.removeChild(chatElements.promptsContainer.firstChild);
    }
  }
}

function renderErrorMessage(chatWrapper) {
  if (!chatWrapper) return;

  let messages = [{ type: "text", text: SORRY_TEXT }];

  if (chatWrapper.dataset.lastQuickReplies) {
    messages.push(getLastQuickReplies(chatWrapper));
  }

  renderChatResponse(chatWrapper, messages);
  showQuickReplies(chatWrapper);
}

function disableInputAndSendIcon(chatWrapper) {
  const chatElements = getChatElements(chatWrapper);

  if (chatElements.chatInput) {
    chatElements.chatInput.disabled = true;
  }
  if (chatElements.sendIcon) {
    chatElements.sendIcon.classList.add('disabled-input');
  }
}

function enableInputAndUpdateSendIcon(chatWrapper) {
  const chatElements = getChatElements(chatWrapper);

  if (chatElements.chatInput) {
    chatElements.chatInput.disabled = false;
  }
  if (chatElements.sendIcon && chatElements.chatInput) {
    if (chatElements.chatInput.value.trim().length > 0) {
      chatElements.sendIcon.classList.remove('disabled-input');
    } else {
      chatElements.sendIcon.classList.add('disabled-input');
    }
  }
}

function updateSendIconState(chatWrapper, inputValue) {
  const chatElements = getChatElements(chatWrapper);

  if (chatElements.sendIcon) {
    if (inputValue && inputValue.trim().length > 0) {
      chatElements.sendIcon.classList.remove('disabled-input');
    } else {
      chatElements.sendIcon.classList.add('disabled-input');
    }
  }
}

function showElement(element) {
  if (element) {
    element.style.display = 'block';
  }
}

function hideElement(element) {
  if (element) {
    element.style.display = 'none';
  }
}

function showQuickReplies(chatWrapper) {
  const quickReplies = JSON.parse(chatWrapper.dataset.lastQuickReplies);
  if (!quickReplies || Object.keys(quickReplies).length === 0) return;

  const chatElements = getChatElements(chatWrapper);
  const innerPromptsElem = chatElements.promptsOuter?.querySelector(".reco-chat-prompts");

  if (innerPromptsElem?.querySelector("li")) {
    chatElements.promptsOuter.style.display = "";
  }
}

function hideQuickReplies(chatWrapper) {
  const chatElements = getChatElements(chatWrapper);
  hideElement(chatElements.promptsOuter);
}

function showLoader(chatWrapper, text = undefined) {
  const chatElements = getChatElements(chatWrapper);
  if (text !== undefined) {
    chatElements.loaderText.textContent = text;
  }
  showElement(chatElements.loaderWrapper);
}

function hideLoader(chatWrapper) {
  const chatElements = getChatElements(chatWrapper);
  chatElements.loaderText.textContent = chatElements.loaderText.dataset.defaultText;
  hideElement(chatElements.loaderWrapper);
}

function showTypingBubbles(chatWrapper) {
  const chatElements = getChatElements(chatWrapper);
  showElement(chatElements.typingBubblesWrapper);
  scrollToBottom(chatWrapper);
}

function hideTypingBubbles(chatWrapper) {
  const chatElements = getChatElements(chatWrapper);
  hideElement(chatElements.typingBubblesWrapper);
}

function scrollToBottom(chatWrapper) {
  const { animateContainer, chatContainer } = getModalElements(chatWrapper);
  if (animateContainer && chatContainer) {
    animateContainer.scrollTo({
      top: chatContainer.scrollHeight,
      behavior: "smooth"
    });
  }
}

function scrollToTop(chatWrapper) {
  const { animateContainer } = getModalElements(chatWrapper);
  if (animateContainer) {
    animateContainer.scrollTo({
      top: 0,
      behavior: "smooth"
    });
  }
}

function scrollToRecentMessage(chatWrapper) {
  const { animateContainer, chatContainer } = getModalElements(chatWrapper);
  const chatElements = getChatElements(chatWrapper);

  if (animateContainer && chatContainer) {
    const agentMsgs = chatContainer.querySelectorAll('.reco-chat-message.chat-message-agent');
    const mostRecentMsgElem = agentMsgs[agentMsgs.length - 1];

    if (mostRecentMsgElem) {
      animateContainer.scrollTo({
        top: (animateContainer.scrollHeight - mostRecentMsgElem.scrollHeight) -
          (chatElements.promptsOuter.scrollHeight + chatElements.inputWrapper.scrollHeight + 125),
        behavior: "smooth",
      });

      // Check if down arrow should be shown after scrolling
      if (chatElements.downArrow) {
        setTimeout(() => {
          checkScrollPosition(animateContainer, chatContainer, chatElements.downArrow);
        }, 500); // Wait for scroll animation to complete
      }
    }
  }
}

function minimizeDrawerHeader(drawerHeader) {
  if (drawerHeader?.classList.contains("mobius-drawer__header")) {
    drawerHeader.classList.add("minimized");
  }
}

function moveIntroToMessages(chatWrapper) {
  const chatElements = getChatElements(chatWrapper);
  const { introElem, messagesInner } = chatElements;

  if (introElem && messagesInner && messagesInner.firstChild !== introElem) {
    const removedIntro = introElem.parentElement.removeChild(introElem);
    messagesInner.prepend(removedIntro);
  }
}

function setupScrollListener(animateContainer, chatContainer, downArrow) {
  if (animateContainer && chatContainer && downArrow && !animateContainer.hasAttribute("data-has-scroll-listener")) {
    animateContainer.setAttribute("data-has-scroll-listener", "true");

    animateContainer.addEventListener("scroll", () => {
      checkScrollPosition(animateContainer, chatContainer, downArrow);
    });

    checkScrollPosition(animateContainer, chatContainer, downArrow);
  }
}

function ensureChatShield(drawerHeader) {
  if (drawerHeader && !drawerHeader.querySelector('.reco-chat-shield')) {
    const shieldDiv = document.createElement('div');
    shieldDiv.className = 'reco-chat-shield';
    drawerHeader.prepend(shieldDiv);
  }
}

function handleBetaLabelMouseEnter(e) {
  if (e.target.matches(".reco-chat-beta-label")) {
    const popover = e.target.querySelector(".reco-chat-beta-popover");
    if (popover) {
      popover.style.display = "block";
    }
  }
}

function handleBetaLabelMouseOut(e) {
  if (e.target.matches(".reco-chat-beta-label")) {
    const popover = e.target.querySelector(".reco-chat-beta-popover");
    if (popover) {
      popover.style.display = "none";
    }
  }
}

function handleDownArrowClick(e) {
  const chatWrapper = e.target.closest(".js-reco-chat-wrapper");
  const product = chatWrapper.dataset.product;

  hideElement(e.target);

  scrollToBottom(chatWrapper);

  publishEvent({
    category: product,
    action: "recommendations_ai_agent_down_arrow_clicked",
  });
}

function conversationHistory(chatWrapper) {
  const messages = []
  chatWrapper.querySelectorAll('.reco-chat-messages-inner .reco-chat-message').forEach(e => {
    messages.push(`${e.classList.contains("chat-message-agent") ? "Agent: " : "User: "} ${e.textContent}`)
  });

  return messages.join("\n");
}

function startLiveChat(chatWrapper) {
  const connectionId = chatWrapper.dataset.chatSessionId;
  if (!connectionId) {
    throw new Error("Couldn't find chat session ID");
  }

  const params = JSON.parse(chatWrapper.dataset.zendeskData);
  params['connectionId'] = connectionId;
  params['product_code'] = getProductCodeLowercase(chatWrapper.dataset.product);
  const queryParams = new URLSearchParams(params).toString();
  const connectUrl = `${chatWrapper.dataset.liveChatUrl}connect?${queryParams}`;

  window.ws = new WebSocket(connectUrl);

  hideQuickReplies(chatWrapper);
  showLoader(chatWrapper, "Connecting you to a live agent...");

  ws.addEventListener("open", () => {
    setTimeout(() => {
      try {
        ws.send(conversationHistory(chatWrapper));
      } catch (error) {
        console.error("Error sending conversation history:", error);
      }
    }, 100); // Small delay to ensure connection is fully established
    onLiveChatStarted(chatWrapper)();
  });
  ws.addEventListener("error", onLiveChatError(chatWrapper));
  ws.addEventListener("message", onLiveChatMessage(chatWrapper));
  ws.addEventListener("close", onLiveChatStopped(chatWrapper));
}

function onLiveChatStarted(chatWrapper) {
  publishEvent({
    category: chatWrapper.dataset.product,
    action: "recommendations_ai_agent_live_chat_started",
    label: chatWrapper.dataset.chatSessionId,
  });

  return () => {
    hideLoader(chatWrapper);
    renderTextAnswer(chatWrapper, { type: "text", text: "You are now connected to a live agent. How we can help you?" });
    showTypingBubbles(chatWrapper);
    chatWrapper.dataset.connectedToLiveChat = 'true';
  }
}

function onLiveChatError(chatWrapper) {
  return () => {
    hideLoader(chatWrapper);
    renderTextAnswer(chatWrapper, { type: "text", text: SORRY_TEXT });
    hideTypingBubbles(chatWrapper);
    renderQuickReplies(chatWrapper, getLastQuickReplies(chatWrapper));
    showQuickReplies(chatWrapper);
    scrollToBottom(chatWrapper);
    chatWrapper.dataset.connectedToLiveChat = 'false';
  }
}

function onLiveChatMessage(chatWrapper) {
  return (event) => {
    try {
      const message = JSON.parse(event.data);
      if (message['type'] == 'agentMessage') {
        hideTypingBubbles(chatWrapper);
        renderTextAnswer(chatWrapper, { type: "text", text: message['content'] });
        scrollToBottom(chatWrapper);

        sendLiveChatMessageSentEvent(chatWrapper, message['content'], "agent");
      }
    } catch (e) {
      console.log(e);
    }
  }
}

function onLiveChatStopped(chatWrapper) {
  return () => {
    if (chatWrapper.dataset.connectedToLiveChat !== 'true') return;

    chatWrapper.dataset.connectedToLiveChat = 'false';
    hideLoader(chatWrapper);
    hideTypingBubbles(chatWrapper);
    renderTextAnswer(chatWrapper, { type: "text", text: "The live chat has ended. If you have any further questions, feel free to ask!" });
    renderQuickReplies(chatWrapper, getLastQuickReplies(chatWrapper));
    showQuickReplies(chatWrapper);
    scrollToBottom(chatWrapper);
    publishEvent({
      category: chatWrapper.dataset.product,
      action: "recommendations_ai_agent_live_chat_ended",
      label: chatWrapper.dataset.chatSessionId,
    });
  };
};

function handleQuickReplyClick(promptLi) {
  const message = promptLi.textContent;
  const chatWrapper = promptLi.closest(".js-reco-chat-wrapper");
  const input = chatWrapper?.querySelector(".js-reco-chat-input");

  if (message === "Chat to an agent") {
    startLiveChat(chatWrapper);
  } else if (chatWrapper && input) {
    sendChatMessage(message, chatWrapper, input);
    setChatInputHeight(input);
  }

  const nextElement = promptLi.nextElementSibling;
  promptLi.remove();
  if (nextElement && nextElement.tagName === "BR") {
    nextElement.remove();
  }
}

function handleSendIconClick(sendIcon) {
  const chatWrapper = sendIcon.closest(".js-reco-chat-wrapper");
  const input = chatWrapper?.querySelector(".js-reco-chat-input");
  const message = input?.value;

  if (chatWrapper && input && message) {
    sendChatMessage(message, chatWrapper, input);
    setChatInputHeight(input);
  }
}

function handleReadMoreClick(readMoreLink) {
  const introParent = readMoreLink.closest(".reco-agent-intro");
  if (introParent) {
    introParent
      .querySelectorAll(".reco-chat-intro-hidden")
      .forEach((el) => showElement(el));

    introParent
      .querySelectorAll(".reco-chat-intro-after-read-hidden")
      .forEach((el) => hideElement(el));

    const chatWrapper = introParent.closest(".js-reco-chat-wrapper");
    const { drawerHeader } = getModalElements(chatWrapper);

    scrollToTop(chatWrapper);
    minimizeDrawerHeader(drawerHeader);
    moveIntroToMessages(chatWrapper);

    const { product } = readMoreLink.dataset;

    publishEvent({
      category: product,
      action: "recommendations_ai_agent_read_more_clicked",
      label: introParent.dataset.userMessage || "",
    });

    readMoreLink.remove();
  }
}

function handleApplicationHandoffClick(handoff) {
  const form = document.querySelector("form");

  // Uncheck all checkboxes in the form
  const checkboxes = form?.querySelectorAll('input[type="checkbox"]');
  checkboxes?.forEach(checkbox => {
    if (checkbox.checked) {
      checkbox.click();
    }
  });

  const chatWrapper = handoff.closest('.js-reco-chat-wrapper');
  const product = chatWrapper.dataset.product;

  const productCheckbox = form?.querySelector(`input#${getProductInputName(product)}_yes`);

  if (productCheckbox)
    productCheckbox.click();

  publishEvent({
    category: chatWrapper.dataset.chatSessionId,
    action: "recommendations_ai_agent_journey_start_clicked",
    label: product,
    value: chatWrapper.querySelectorAll(".reco-chat-messages-inner .chat-message-user").length,
  });

  form.submit();
}

function handleCtaButtonClick(e, ctaButton) {
  e.preventDefault();
  const modalContainer = ctaButton.closest(
    ".js-reco-agent-ai-cta",
  )?.parentElement;
  const modal = modalContainer?.querySelector(
    'div[data-react-app="Frontoffice.DialogueModal"]',
  );
  const openButton = modal?.querySelector("button");

  const chatWrapper = modal?.querySelector('.js-reco-chat-wrapper');

  if (openButton) openButton.click();

  if (!chatWrapper?.dataset.chatId) {
    const { drawerHeader } = getModalElements(chatWrapper);
    const product = chatWrapper.dataset.product;

    ensureChatShield(drawerHeader);

    startChat(chatWrapper);

    publishEvent({
      category: product,
      action: "recommendations_ai_agent_new_cta_clicked",
      label: ctaButton.textContent.trim(),
    });
  }
}

function handleClick(e) {
  if (e.target.matches(".js-reco-chat-down-arrow")) {
    handleDownArrowClick(e);
    return;
  }

  const promptLi = e.target.closest(
    ".js-reco-chat-wrapper .reco-chat-prompts li",
  );
  if (promptLi) {
    handleQuickReplyClick(promptLi);
    return;
  }

  const sendIcon = e.target.closest(
    ".js-reco-chat-wrapper .reco-chat-input-wrapper .reco-chat-send-icon",
  );
  if (sendIcon && !sendIcon.classList.contains("disabled-input")) {
    handleSendIconClick(sendIcon);
    return;
  }

  const readMoreLink = e.target.closest(".reco-chat-read-more");
  if (readMoreLink) {
    handleReadMoreClick(readMoreLink);
    return;
  }

  const handoff = e.target.closest("#products-application-handoff");
  if (handoff) {
    handleApplicationHandoffClick(handoff);
    return;
  }

  const ctaButton = e.target.closest(".js-reco-agent-ai-cta button");
  if (ctaButton) {
    handleCtaButtonClick(e, ctaButton);
    return;
  }
}

function handleKeypress(e) {
  if (e.target.matches(".js-reco-chat-input") && e.key === "Enter") {
    e.preventDefault();
    const input = e.target;
    const message = input.value;
    const wrapper = input.closest(".js-reco-chat-wrapper");

    if (wrapper) {
      sendChatMessage(message, wrapper, input);
      setChatInputHeight(input);
    }
  }
}

function handleInput(e) {
  if (e.target.matches(".js-reco-chat-input")) {
    const input = e.target;
    const chatWrapper = input.closest(".js-reco-chat-wrapper");

    if (chatWrapper && !input.disabled) {
      updateSendIconState(chatWrapper, input.value);
    }
    setChatInputHeight(input);
  }
}

async function sendMessageToApi({ chatId, authentication, userMessage }) {
  const response = await fetch("/insurance_advisor/send_message", {
    headers: {
      "content-type": "application/json",
      accept: "application/json",
    },
    method: "POST",
    body: JSON.stringify({
      chat_id: chatId,
      authentication: authentication,
      user_message: userMessage
    })
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  const data = await response.json();

  if (data.error) {
    throw new Error(data.error);
  }

  return data;
}

async function createChatApi({ primaryTrade, sessionId, productName, productCode, experiments }) {
  const response = await fetch("/insurance_advisor/create_chat", {
    headers: {
      "content-type": "application/json",
      accept: "application/json",
    },
    method: "POST",
    body: JSON.stringify({
      primary_trade: primaryTrade,
      session_id: sessionId,
      product_name: productName,
      product_code: productCode,
      experiments: experiments
    })
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  const data = await response.json();

  if (data.error) {
    throw new Error(data.error);
  }

  return data;
}

function sendLiveChatMessageSentEvent(chatWrapper, message, actor) {
  if (
    window.snowplowHelper &&
    window.snowplowHelper.trackFrontofficeStructEvent &&
    chatWrapper.dataset.chatSessionId
  ) {

    const site = document.querySelector('meta[name="site"]')?.getAttribute("content") || "";
    const pageTitle = document.querySelector('title')?.textContent || null;
    const payload = {
      page_title: pageTitle,
      chat_session_id: chatWrapper.dataset.chatSessionId,
      product: chatWrapper.dataset?.product || null,
      message,
      actor,
    }
    console.log('Sending IA live chat message sent event:', payload);
    window.snowplowHelper.trackUnstructEvent(
      "iglu:com.simplybusiness/ia_live_chat_message_sent/jsonschema/1-0-0",
      payload,
      window.snowplowHelper.distributionChannelContext({
        version: "1-0-0",
        serviceChannelIdentifier: site,
      })
    );
  }
}

function publishEvent({ category, action, label, value, property, serviceChannelIdentifier }) {
  if (!window.snowplowHelper?.trackFrontofficeStructEvent) {
    return;
  }

  if (!serviceChannelIdentifier) {
    const siteMeta = document.querySelector('meta[name="site"]');
    serviceChannelIdentifier = siteMeta ? siteMeta.getAttribute("content") : "";
  }

  const eventPayload = {
    category,
    action,
    property: property || window.location.href,
    serviceChannelIdentifier,
  };

  // Only add optional fields if they are provided
  if (label !== undefined) {
    eventPayload.label = label;
  }
  if (value !== undefined) {
    eventPayload.value = value;
  }

  window.snowplowHelper.trackFrontofficeStructEvent(eventPayload);
}

const isApprovedAnswer = (message) => {
  return message.metadata?.approved_answer == true;
}

function shouldShowApplicationHandoff(message, startApplicationExperiment) {
  return startApplicationExperiment === "true" &&
    isApprovedAnswer(message);
}

function generateSessionId(product) {
  return `${getProductCode(product)}::${crypto.randomUUID()}`;
}

function getProductCode(product) {
  return productIdMapping[product];
}

function getProductCodeLowercase(product) {
  return getProductCode(product)?.toLowerCase();
}

function getLastQuickReplies(chatWrapper) {
  return JSON.parse(chatWrapper.dataset.lastQuickReplies ?? "{}");
}

function setChatInputHeight(input) {
  input.style.height = "0";
  input.style.height = `${input.scrollHeight + 4}px`;
}

function checkScrollPosition(animateContainer, chatContainer, downArrow) {
  if (!animateContainer || !chatContainer || !downArrow) return;

  const isScrolledToBottom =
    animateContainer.scrollTop + animateContainer.clientHeight >= chatContainer.scrollHeight - 25;

  if (isScrolledToBottom) {
    hideElement(downArrow);
  } else {
    showElement(downArrow);
  }
}

function getProductInputName(product) {
  const productCode = getProductCode(product);
  if (productCode === "IM") {
    return "inland_marine_coverage";
  } else if (productCode === "GL") {
    return "general_liability_coverage";
  } else if (productCode === "WC") {
    return "workers_compensation_coverage";
  } else if (productCode === "BOP") {
    return "business_owners_policy_coverage";
  } else if (productCode === "PL") {
    return "professional_liability_coverage";
  } else {
    return "";
  }
}

document.addEventListener("DOMContentLoaded", function () {
  if (!document.body.className.includes('usa'))
    return;

  document.body.addEventListener("mouseenter", handleBetaLabelMouseEnter);
  document.body.addEventListener("mouseout", handleBetaLabelMouseOut);
  document.body.addEventListener("click", handleClick);
  document.body.addEventListener("keypress", handleKeypress);
  document.body.addEventListener("input", handleInput);

  function setupScrollListeners(container) {
    const chatWrappers = container.querySelectorAll(".js-reco-chat-wrapper");

    chatWrappers.forEach((chatWrapper) => {
      const { animateContainer, chatContainer } = getModalElements(chatWrapper);
      const { downArrow } = getChatElements(chatWrapper);

      setupScrollListener(animateContainer, chatContainer, downArrow);
    });
  }

  const observer = new MutationObserver((mutationsList) => {
    for (const mutation of mutationsList) {
      if (mutation.type === "childList" && mutation.addedNodes.length > 0) {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === Node.ELEMENT_NODE) {
            // Setup scroll listeners for any new chat wrappers

          document.querySelectorAll('.js-reco-chat-wrapper').forEach(chatWrapper => {
            const dialogueContent = chatWrapper.closest('.mobius-dialogue-modal__styled-drawer-content');
            if (dialogueContent) {
              const intermediateDiv = chatWrapper.parentElement;
              // Only move if intermediateDiv is not dialogueContent and intermediateDiv has no other children
              if (
              intermediateDiv &&
              intermediateDiv !== dialogueContent &&
              intermediateDiv.children.length === 1 // Only chatWrapper is present
              ) {
                dialogueContent.appendChild(chatWrapper);
                intermediateDiv.remove();
                disableInputAndSendIcon(chatWrapper);
              }
            }

          });
            setupScrollListeners(node);

            const dialogs = node.matches(
              'div[data-react-app="Frontoffice.DialogueModal"][data-react-props*="js-reco-chat-wrapper"] dialog',
            )
              ? [node]
              : node.querySelectorAll(
                'div[data-react-app="Frontoffice.DialogueModal"][data-react-props*="js-reco-chat-wrapper"] dialog',
              );

            dialogs.forEach((dialog) => {
              if (!dialog.hasAttribute("data-has-close-listener")) {
                dialog.setAttribute("data-has-close-listener", "true");

                dialog.addEventListener("close", () => {
                  publishEvent({
                    category: window.location.href,
                    action: "recommendations_ai_agent_drawer_closed",
                  });
                });
              }
            });
          }
        });
      }
    }
  });

  observer.observe(document.body, { childList: true, subtree: true });

  setupScrollListeners(document.body);
});
